import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Activity, Baby, Hand, Apple, MessageCircle, ArrowRight } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import { CTABanner } from "../components/CTABanner";
import { SEO } from "../components/SEO";
import { SERVICES } from "../data/siteData";

const spring = { type: "spring", stiffness: 200, damping: 20 };
const ICONS = { Activity, Baby, Hand, Apple, MessageCircle };
const CHIP_STYLES = {
  sunny: "bg-sunny-soft text-sunny-dark",
  teal: "bg-teal-soft text-teal-dark",
  butter: "bg-butter-soft text-navy",
  lilac: "bg-lilac-soft text-navy",
  leaf: "bg-leaf-soft text-leaf-dark",
};
const ICON_STYLES = {
  sunny: "bg-sunny text-navy",
  teal: "bg-teal text-navy",
  butter: "bg-butter text-navy",
  lilac: "bg-lilac text-navy",
  leaf: "bg-leaf text-navy",
};
const CARD_BG = {
  sunny: "bg-sunny-soft",
  teal: "bg-teal-soft",
  butter: "bg-butter-soft",
  lilac: "bg-lilac-soft",
  leaf: "bg-leaf-soft",
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Pediatric Therapy Services in Nassau, Bahamas | Thrive"
        description="Physiotherapy, occupational therapy, speech & language therapy and feeding therapy for children aged 0-18 in Nassau, Bahamas - all under one roof at Thrive."
      />
      <PageHeader
        testId="services-header"
        eyebrow="Our Services"
        title="Every child is different. Their care should be too."
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid sm:grid-cols-2 gap-6">
          {SERVICES.map((service, i) => {
            const Icon = ICONS[service.icon];
            return (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ ...spring, delay: i * 0.08 }}
              >
                <Link
                  to={`/services/${service.slug}`}
                  data-testid={`services-card-${service.slug}`}
                  className="group flex flex-col bg-white rounded-[2rem] border border-border overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-navy/10 h-full"
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.fullName}
                      loading="lazy"
                      className="w-full h-full object-cover object-[center_20%] transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className={`p-8 flex flex-col flex-1 ${CARD_BG[service.color]}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-11 h-11 rounded-2xl ${ICON_STYLES[service.color]} flex items-center justify-center`}>
                        <Icon className="w-5 h-5" strokeWidth={2.25} />
                      </span>
                      <span className={`text-[11px] font-extrabold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full ${CHIP_STYLES[service.color]}`}>
                        {service.tagline}
                      </span>
                    </div>
                    <h2 className="mt-4 font-display text-xl sm:text-2xl font-semibold text-navy">{service.fullName}</h2>
                    <p className="mt-2.5 text-sm text-navy-light leading-relaxed flex-1">{service.description}</p>
                    <span className="mt-5 inline-flex items-center gap-2 font-extrabold text-sm text-teal-dark">
                      Explore this service
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={2.5} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={spring}
          data-testid="services-guide-note"
          className="mt-12 text-center text-base text-navy-light"
        >
          Not sure which fits your child?{" "}
          <Link to="/start" data-testid="services-start-link" className="font-extrabold text-teal-dark hover:underline">
            Tell us what you're noticing
          </Link>{" "}
          and we'll point you in the right direction.
        </motion.p>
      </section>

      <CTABanner title="Not sure which therapy fits?" subtitle="Book an evaluation and we'll figure out the best path for your child - together." />
    </div>
  );
}
